import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Award, Upload, Trash2, Eye, Download, Plus, Search, X, FileText,
  Calendar, Building2, User, ChevronRight, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CERT_TYPES = [
  'Academic Degree',
  'Diploma',
  'Professional Certification',
  'Safety & Security',
  'Computer / IT',
  'Technical Skill',
  'Other',
];

function getFileIcon(ext) {
  if (!ext) return <FileText className="w-8 h-8 text-gray-400" />;
  const e = ext.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(e))
    return null; // will show image preview
  if (e === 'pdf')
    return <div className="flex flex-col items-center gap-1"><FileText className="w-10 h-10 text-red-400" /><span className="text-xs font-bold text-red-500">PDF</span></div>;
  return <div className="flex flex-col items-center gap-1"><FileText className="w-10 h-10 text-blue-400" /><span className="text-xs font-bold text-blue-500 uppercase">{e}</span></div>;
}

function isImage(ext) {
  return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes((ext || '').toLowerCase());
}

export default function CertificatesPage() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEmpId, setFilterEmpId] = useState('');
  const [viewCert, setViewCert] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const token = localStorage.getItem('crm_token');
  const storedUser = localStorage.getItem('crm_user');
  const currentUser = storedUser ? JSON.parse(storedUser) : {};
  const isHR = ['cmd', 'super_admin'].includes(currentUser.role) || currentUser.department === 'hr';

  const [form, setForm] = useState({
    employeeId: isHR ? '' : (currentUser.employeeId || ''),
    employeeName: isHR ? '' : ((currentUser.firstName || '') + ' ' + (currentUser.lastName || '')).trim(),
    certificateName: '',
    issuingOrganization: '',
    issueDate: '',
    expiryDate: '',
    notes: '',
    file: null,
  });

  useEffect(() => {
    fetchCerts();
  }, []);

  async function fetchCerts() {
    setLoading(true);
    try {
      const empFilter = isHR ? '' : (currentUser.employeeId || '');
      const url = empFilter
        ? `${API}/certificates?employeeId=${encodeURIComponent(empFilter)}`
        : `${API}/certificates`;
      const res = await axios.get(url, { headers: { Authorization: 'Bearer ' + token } });
      setCertificates(res.data.certificates || []);
    } catch (e) {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!form.file) { toast.error('Please select a file'); return; }
    if (!form.employeeId.trim()) { toast.error('Employee ID is required'); return; }
    if (!form.certificateName.trim()) { toast.error('Certificate name is required'); return; }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('employeeId', form.employeeId.trim());
      fd.append('employeeName', form.employeeName.trim());
      fd.append('certificateName', form.certificateName.trim());
      fd.append('issuingOrganization', form.issuingOrganization.trim());
      fd.append('issueDate', form.issueDate);
      fd.append('expiryDate', form.expiryDate);
      fd.append('notes', form.notes.trim());
      fd.append('uploadedByName', ((currentUser.firstName || '') + ' ' + (currentUser.lastName || '')).trim());

      await axios.post(`${API}/certificates/upload`, fd, {
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Certificate uploaded successfully');
      setShowUpload(false);
      setForm(prev => ({
        ...prev,
        certificateName: '', issuingOrganization: '', issueDate: '',
        expiryDate: '', notes: '', file: null,
      }));
      fetchCerts();
    } catch (e) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(cert) {
    if (!window.confirm(`Delete "${cert.certificateName}"?`)) return;
    setDeleting(cert.id);
    try {
      await axios.delete(`${API}/certificates/${cert.id}`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      toast.success('Certificate deleted');
      setCertificates(prev => prev.filter(c => c.id !== cert.id));
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(null);
    }
  }

  const filtered = certificates.filter(c => {
    const term = searchTerm.toLowerCase();
    const matchSearch =
      !term ||
      c.certificateName?.toLowerCase().includes(term) ||
      c.employeeName?.toLowerCase().includes(term) ||
      c.issuingOrganization?.toLowerCase().includes(term) ||
      c.employeeId?.toLowerCase().includes(term);
    const matchEmp = !filterEmpId || c.employeeId === filterEmpId;
    return matchSearch && matchEmp;
  });

  const uniqueEmployees = [...new Map(certificates.map(c => [c.employeeId, c])).values()];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Award className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Certificates</h1>
              <p className="text-sm text-gray-500">Employee qualification & achievement certificates</p>
            </div>
          </div>
          <button
            data-testid="upload-certificate-btn"
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Certificate
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search certificates, employees, organisations..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
              data-testid="certificate-search"
            />
          </div>
          {isHR && uniqueEmployees.length > 0 && (
            <select
              value={filterEmpId}
              onChange={e => setFilterEmpId(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
              data-testid="employee-filter"
            >
              <option value="">All Employees</option>
              {uniqueEmployees.map(u => (
                <option key={u.employeeId} value={u.employeeId}>
                  {u.employeeName} ({u.employeeId})
                </option>
              ))}
            </select>
          )}
          <span className="text-sm text-gray-500 ml-auto">
            {filtered.length} certificate{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-10">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-amber-600 mr-2" />
            <span className="text-gray-500">Loading certificates...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
              <Award className="w-10 h-10 text-amber-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No certificates yet</h3>
            <p className="text-sm text-gray-400 mb-4">
              {searchTerm ? 'No results match your search.' : 'Upload the first certificate to get started.'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowUpload(true)}
                className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
              >
                <Upload className="w-4 h-4" />
                Upload Certificate
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(cert => (
              <CertCard
                key={cert.id}
                cert={cert}
                isHR={isHR}
                onView={() => setViewCert(cert)}
                onDelete={() => handleDelete(cert)}
                deleting={deleting === cert.id}
                currentUserId={currentUser.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <UploadModal
          form={form}
          setForm={setForm}
          isHR={isHR}
          fileRef={fileRef}
          uploading={uploading}
          onSubmit={handleUpload}
          onClose={() => { setShowUpload(false); setForm(prev => ({ ...prev, file: null })); }}
        />
      )}

      {/* View Modal */}
      {viewCert && (
        <ViewModal
          cert={viewCert}
          onClose={() => setViewCert(null)}
          token={token}
        />
      )}
    </div>
  );
}

// ---------- Certificate Card ----------
function CertCard({ cert, isHR, onView, onDelete, deleting, currentUserId }) {
  const isImg = isImage(cert.fileExt);
  const viewUrl = `${API}/certificates/${cert.id}/view`;
  const downloadUrl = `${API}/certificates/${cert.id}/download`;

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group"
      data-testid={`cert-card-${cert.id}`}
    >
      {/* Thumbnail */}
      <div
        className="h-40 bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center cursor-pointer relative"
        onClick={onView}
      >
        {isImg ? (
          <img
            src={viewUrl}
            alt={cert.certificateName}
            className="w-full h-full object-cover"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div
          className="flex flex-col items-center gap-1"
          style={{ display: isImg ? 'none' : 'flex' }}
        >
          {getFileIcon(cert.fileExt)}
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2 mb-1">
          {cert.certificateName}
        </h3>
        {cert.issuingOrganization && (
          <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
            <Building2 className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{cert.issuingOrganization}</span>
          </p>
        )}
        {isHR && (
          <p className="text-xs text-blue-600 flex items-center gap-1 mb-1">
            <User className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{cert.employeeName} · {cert.employeeId}</span>
          </p>
        )}
        {cert.issueDate && (
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            {cert.issueDate}
            {cert.expiryDate && <span> – {cert.expiryDate}</span>}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="px-3 pb-3 flex gap-2">
        <button
          onClick={onView}
          className="flex-1 flex items-center justify-center gap-1 bg-amber-50 hover:bg-amber-100 text-amber-700 py-1.5 rounded-lg text-xs font-medium transition-colors"
          data-testid={`view-cert-${cert.id}`}
        >
          <Eye className="w-3.5 h-3.5" />
          View
        </button>
        <a
          href={downloadUrl}
          download={cert.originalName || cert.certificateName}
          className="flex items-center justify-center gap-1 px-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 py-1.5 rounded-lg text-xs font-medium transition-colors"
          data-testid={`download-cert-${cert.id}`}
          title="Download certificate"
        >
          <Download className="w-3.5 h-3.5" />
        </a>
        {isHR && (
          <button
            onClick={onDelete}
            disabled={deleting}
            className="flex items-center justify-center gap-1 px-2.5 bg-red-50 hover:bg-red-100 text-red-600 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            data-testid={`delete-cert-${cert.id}`}
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Upload Modal ----------
function UploadModal({ form, setForm, isHR, fileRef, uploading, onSubmit, onClose }) {
  const [dragOver, setDragOver] = useState(false);

  function handleFileDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setForm(prev => ({ ...prev, file: f }));
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <Upload className="w-4 h-4 text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Upload Certificate</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-4">
          {/* File Drop Zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-amber-400 bg-amber-50' :
              form.file ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
            }`}
            data-testid="file-drop-zone"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              className="hidden"
              onChange={e => setForm(prev => ({ ...prev, file: e.target.files[0] || null }))}
            />
            {form.file ? (
              <div className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Award className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-sm font-medium text-green-700">{form.file.name}</p>
                <p className="text-xs text-gray-400">{(form.file.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-gray-300" />
                <p className="text-sm text-gray-600 font-medium">Click or drag to upload</p>
                <p className="text-xs text-gray-400">JPG, PNG, PDF, DOCX · Max 10 MB</p>
              </div>
            )}
          </div>

          {/* Employee Details */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Employee ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="DBK-0014"
                value={form.employeeId}
                onChange={e => setForm(prev => ({ ...prev, employeeId: e.target.value }))}
                readOnly={!isHR}
                className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 ${!isHR ? 'bg-gray-50 text-gray-500' : ''}`}
                data-testid="cert-employee-id"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Employee Name</label>
              <input
                type="text"
                placeholder="Full name"
                value={form.employeeName}
                onChange={e => setForm(prev => ({ ...prev, employeeName: e.target.value }))}
                readOnly={!isHR}
                className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 ${!isHR ? 'bg-gray-50 text-gray-500' : ''}`}
              />
            </div>
          </div>

          {/* Certificate Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Certificate Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. MS-CIT Certificate, Fire Safety Diploma"
              value={form.certificateName}
              onChange={e => setForm(prev => ({ ...prev, certificateName: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="cert-name-input"
            />
          </div>

          {/* Issuing Organization */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Issuing Organisation</label>
            <input
              type="text"
              placeholder="e.g. MKCL, PACE CTTA, Panjab University"
              value={form.issuingOrganization}
              onChange={e => setForm(prev => ({ ...prev, issuingOrganization: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="cert-org-input"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Issue Date</label>
              <input
                type="date"
                value={form.issueDate}
                onChange={e => setForm(prev => ({ ...prev, issueDate: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Expiry Date <span className="text-gray-400">(optional)</span></label>
              <input
                type="date"
                value={form.expiryDate}
                onChange={e => setForm(prev => ({ ...prev, expiryDate: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes <span className="text-gray-400">(optional)</span></label>
            <textarea
              rows={2}
              placeholder="Additional details..."
              value={form.notes}
              onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="submit-upload-cert"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="w-4 h-4" /> Upload Certificate</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- View Modal ----------
function ViewModal({ cert, onClose, token }) {
  const isImg = isImage(cert.fileExt);
  const viewUrl = `${API}/certificates/${cert.id}/view`;
  const downloadUrl = `${API}/certificates/${cert.id}/download`;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="font-bold text-gray-900 text-base">{cert.certificateName}</h2>
            <p className="text-xs text-gray-500">
              {cert.employeeName} · {cert.employeeId}
              {cert.issuingOrganization ? ` · ${cert.issuingOrganization}` : ''}
              {cert.issueDate ? ` · ${cert.issueDate}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={downloadUrl}
              download={cert.originalName || cert.certificateName}
              className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
              data-testid="modal-download-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </a>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50">
          {isImg ? (
            <img
              src={viewUrl}
              alt={cert.certificateName}
              className="max-w-full max-h-[65vh] object-contain rounded-lg shadow"
            />
          ) : cert.fileExt === 'pdf' ? (
            <iframe
              src={viewUrl}
              title={cert.certificateName}
              className="w-full h-[65vh] rounded-lg border"
            />
          ) : (
            <div className="flex flex-col items-center gap-4 py-10">
              <FileText className="w-16 h-16 text-gray-300" />
              <p className="text-gray-500 text-sm">Preview not available for this file type.</p>
              <a
                href={downloadUrl}
                download={cert.originalName}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                Download to view
              </a>
            </div>
          )}
        </div>

        {/* Footer Notes */}
        {cert.notes && (
          <div className="px-5 py-3 bg-amber-50 border-t border-amber-100 text-xs text-amber-800 flex-shrink-0">
            <span className="font-medium">Notes:</span> {cert.notes}
          </div>
        )}
      </div>
    </div>
  );
}
